/**
 * Markdown → HTML Tool
 *
 * Converts Markdown to HTML using the `marked` library.
 */

import { marked } from 'marked';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const MarkdownToHtmlTool: ITransformerTool = {
  id: 'markdown-to-html',
  name: 'Markdown → HTML',
  description: 'Convert Markdown to HTML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['markdown', 'html', 'convert', 'transform', 'render', 'md'],

  seo: {
    slug: 'markdown-to-html',
    pageTitle: 'Markdown to HTML Converter - Free Online Tool | Prism',
    metaDescription: 'Convert Markdown to HTML instantly. Supports GFM tables, code blocks, and task lists. Fast, private, runs entirely in your browser.',
    keywords: ['markdown to html', 'markdown to html converter', 'convert markdown online', 'md to html', 'markdown renderer'],
    relatedTools: ['markdown-preview', 'html-format'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.MARKDOWN, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.HTML,

  attributes: [
    {
      name: 'gfm',
      type: 'boolean',
      label: 'GitHub Flavored Markdown',
      defaultValue: true,
      description: 'Enable GitHub Flavored Markdown (tables, strikethrough, task lists)',
    },
    {
      name: 'breaks',
      type: 'boolean',
      label: 'Line Breaks',
      defaultValue: false,
      description: 'Convert single newlines to <br> tags',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const gfm = config.gfm !== false;
    const breaks = Boolean(config.breaks);

    try {
      const result = await marked(input, { gfm, breaks });
      return result;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to convert Markdown';
      throw ToolError.execution(msg);
    }
  },
};
